import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useLayoutEffect, useRef } from 'react';
import { Toast, ToastType } from '../components/Toast';

// Create an isomorphic useLayoutEffect hook
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

interface ToastContextType {
  showToast: (message: ReactNode, type: ToastType, onUndo?: () => void) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastMessage {
  id: number;
  message: ReactNode;
  type: ToastType;
  onUndo?: () => void;
  text?: string; // Text representation for deduplication
}

// Queue item type that includes text field
interface ToastQueueItem {
  message: ReactNode;
  type: ToastType;
  onUndo?: () => void;
  text?: string;
}

// Convert ReactNode to string for comparison
const getTextFromReactNode = (node: ReactNode): string => {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return node.toString();
  if (node === null || node === undefined) return '';
  if (React.isValidElement(node)) {
    const children = node.props.children;
    if (typeof children === 'string') return children;
    if (Array.isArray(children)) return children.map(getTextFromReactNode).join('');
  }
  if (Array.isArray(node)) return node.map(getTextFromReactNode).join('');
  return '';
};

const MAX_VISIBLE_TOASTS = 5; // Maximum number of toasts to keep in history
const DUPLICATE_PREVENTION_TIME = 3000; // Time in ms to prevent duplicate toasts

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const toastQueueRef = useRef<ToastQueueItem[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const recentToastsRef = useRef<Map<string, number>>(new Map()); // Track recent toast messages

  const showToast = useCallback((message: ReactNode, type: ToastType, onUndo?: () => void) => {
    // Convert message to text for deduplication
    const messageText = getTextFromReactNode(message);
    
    // Check if we've shown this message recently
    const now = Date.now();
    const lastShown = recentToastsRef.current.get(messageText);
    
    if (lastShown && now - lastShown < DUPLICATE_PREVENTION_TIME) {
      console.debug('Preventing duplicate toast:', messageText);
      return; // Don't show duplicate toast
    }
    
    // Add to queue and update timestamp
    recentToastsRef.current.set(messageText, now);
    toastQueueRef.current.push({ message, type, onUndo, text: messageText });
    
    // Force a re-render to process the queue
    setToasts(prev => [...prev]);
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (toastQueueRef.current.length > 0) {
      const newToasts = toastQueueRef.current.map(toast => ({
        id: Date.now() + Math.random(),
        ...toast
      }));
      setToasts(prev => {
        const updatedToasts = [...prev, ...newToasts];
        // Keep only the last MAX_VISIBLE_TOASTS
        return updatedToasts.slice(-MAX_VISIBLE_TOASTS);
      });
      toastQueueRef.current = [];

      // Scroll to the bottom when new toasts are added
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
    }
  }, [toasts]);

  // Cleanup old entries in the recent toasts map
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      recentToastsRef.current.forEach((timestamp, key) => {
        if (now - timestamp > DUPLICATE_PREVENTION_TIME) {
          recentToastsRef.current.delete(key);
        }
      });
    }, DUPLICATE_PREVENTION_TIME);
    
    return () => clearInterval(cleanupInterval);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div 
        className="fixed bottom-0 right-0 p-4 z-50"
        style={{
          maxWidth: '400px',
          width: 'auto'
        }}
      >
        <div 
          ref={containerRef}
          className={`overflow-y-auto max-h-[min(600px,80vh)] space-y-3 scrollbar-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent scrollbar-hover:scrollbar-thumb-gray-500 dark:scrollbar-hover:scrollbar-thumb-gray-500 rounded-lg ${toasts.length > 0 ? 'bg-gray-900/5 dark:bg-gray-100/5 p-3' : ''}`}
          style={{
            WebkitOverflowScrolling: 'touch',
            msOverflowStyle: '-ms-autohiding-scrollbar',
            overscrollBehavior: 'contain'
          }}
        >
          {toasts.map(toast => (
            <div key={toast.id}>
              <Toast
                message={toast.message}
                type={toast.type}
                onClose={() => removeToast(toast.id)}
                onUndo={toast.onUndo}
              />
            </div>
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}; 